import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Descriptions,
  Drawer,
  Empty,
  Form,
  Input,
  Col,
  Row,
  Select,
  Skeleton,
  Space,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  AppstoreOutlined,
  BankOutlined,
  CheckCircleOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import useAppConfig from "hooks/access-control/useAppConfig";
import GooglePlacesAutocompleteInput from "common/GooglePlacesAutocompleteInput";
import CopyableText from "common/CopyableText";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import paths from "routes/paths";
import {
  fetchWorkspace,
  updateWorkspaceProperties,
  updateWorkspaceOrganization,
} from "services/workspaceService";
import olreadiaService from "services/olreadiaService";
import {
  organizationName,
  formatDateTime,
  hasAdminAccess,
  membershipUserId,
  readable,
} from "./workspaceUtils";
import { OLIVEEB_COLORS, workspaceButtonStyles, workspaceStyles } from "./colors";
import { addressFieldsFromPlaceDetails } from "utils/googlePlaces";
import "pages/OrganizationsPage.css";

const { Text, Title } = Typography;

const pageContent = (data) => (Array.isArray(data) ? data : data?.content || []);

const trimValue = (value) => String(value || "").trim();

const isPersonalAccount = (account) =>
  String(account?.accountType || "").toUpperCase() === "PERSONAL";

const entityContact = (entity) => {
  const contact = entity?.contactInformation || {};
  return [
    contact.primaryEmail || entity?.email,
    contact.primaryPhone || entity?.phoneNumber || entity?.phone,
    contact.websiteUrl || entity?.websiteUrl || entity?.website,
  ]
    .filter(Boolean)
    .join(" • ");
};

const locationTypeLabel = (value) =>
  value?.label || value?.name || readable(value?.code || value, "Location type");

const organizationLocations = (entity) =>
  entity?.businessLocations || entity?.organizationLocations || entity?.locations || [];

const selectedOrganizationLocation = (entity, organizationLocationId) => {
  const locations = organizationLocations(entity);
  if (!locations.length) return null;
  return locations.find((location) => String(location.id || "") === String(organizationLocationId || "")) || locations[0];
};

const organizationLocationDisplay = (location) =>
  [location?.name, locationText(location)].filter(Boolean).join(" - ");

const locationText = (entity) => {
  const location = entity?.locationInformation || {};
  return [
    location.addressLine1,
    location.addressLine2,
    location.city,
    location.state,
    location.zipCode,
    location.country,
  ]
    .filter(Boolean)
    .join(", ");
};

const locationCoordinateText = (entity) => {
  const location = entity?.locationInformation || {};
  const latitude = Number(location.latitude);
  const longitude = Number(location.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return "";
  return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
};

const locationPayloadFromValues = (values = {}) => ({
  name: trimValue(values.name),
  description: trimValue(values.description),
  organizationLocationTypeId: values.organizationLocationTypeId,
  status: values.status || "ACTIVE",
  contactInformation: {
    primaryEmail: trimValue(values.primaryEmail),
    secondaryEmail: trimValue(values.secondaryEmail),
    primaryPhone: trimValue(values.primaryPhone),
    secondaryPhone: trimValue(values.secondaryPhone),
    websiteUrl: trimValue(values.websiteUrl),
    contactPersonName: trimValue(values.contactPersonName),
    contactPersonTitle: trimValue(values.contactPersonTitle),
  },
  locationInformation: {
    addressLine1: trimValue(values.addressLine1),
    addressLine2: trimValue(values.addressLine2),
    city: trimValue(values.city),
    county: trimValue(values.county),
    state: trimValue(values.state),
    zipCode: trimValue(values.zipCode),
    country: trimValue(values.country) || "USA",
    latitude: values.latitude || null,
    longitude: values.longitude || null,
    placeId: values.placeId || null,
  },
});

const organizationLocationFormValues = (entity = {}) => {
  const contact = entity.contactInformation || {};
  return {
    name: "",
    organizationLocationTypeId:
      entity.organizationLocationTypeId ||
      entity.businessLocationType?.id ||
      entity.organizationLocationType?.id ||
      "",
    status: "ACTIVE",
    primaryEmail: contact.primaryEmail || entity.email || "",
    secondaryEmail: contact.secondaryEmail || "",
    primaryPhone: contact.primaryPhone || entity.phoneNumber || entity.phone || "",
    secondaryPhone: contact.secondaryPhone || "",
    websiteUrl: contact.websiteUrl || entity.websiteUrl || entity.website || "",
    contactPersonName: contact.contactPersonName || "",
    contactPersonTitle: contact.contactPersonTitle || "",
    ...organizationAddressFormValues(entity),
  };
};

const organizationAddressFormValues = (entity = {}) => {
  const location = entity.locationInformation || {};
  return {
    addressLine1: location.addressLine1 || "",
    addressLine2: location.addressLine2 || "",
    city: location.city || "",
    county: location.county || "",
    state: location.state || "",
    zipCode: location.zipCode || "",
    country: location.country || "USA",
    latitude: location.latitude || null,
    longitude: location.longitude || null,
    placeId: location.placeId || null,
  };
};

const WorkspaceOrganizationPage = () => {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshWorkspaces } = useWorkspace();
  const { appConfig, loading: appConfigLoading } = useAppConfig();
  const actorId = user?.id || user?.userId || user?.uid || "";
  const [account, setAccount] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [locationTypes, setLocationTypes] = useState([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState("");
  const [changeOrganizationOpen, setChangeOrganizationOpen] = useState(false);
  const [organizationTab, setOrganizationTab] = useState("select");
  const [createOrganizationOpen, setCreateOrganizationOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [savingLink, setSavingLink] = useState(false);
  const [creating, setCreating] = useState(false);
  const [locationDrawerOpen, setLocationDrawerOpen] = useState(false);
  const [savingLocation, setSavingLocation] = useState(false);
  const [savingLocationSelection, setSavingLocationSelection] = useState(false);
  const [sameAsOrganization, setSameAsOrganization] = useState(false);
  const [locationAddressSearch, setLocationAddressSearch] = useState("");
  const [createForm] = Form.useForm();
  const [locationForm] = Form.useForm();
  const googleMapsApiKey =
    appConfig?.googleMapsApiKey ||
    appConfig?.google?.mapsApiKey ||
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    "";
  const myMembership = useMemo(
    () => (account?.users || []).find((member) => String(membershipUserId(member)) === String(actorId)) || null,
    [account?.users, actorId],
  );
  const canManageOrganization = hasAdminAccess(myMembership);

  const locationTypeOptions = useMemo(
    () =>
      locationTypes.map((type) => ({
        value: type.id,
        label: type.label || type.code || type.id,
      })),
    [locationTypes],
  );

  const loadData = useCallback(async () => {
    if (!workspaceId || !actorId) return;

    try {
      setLoading(true);
      const nextAccount = await fetchWorkspace(workspaceId, actorId);
      setAccount(nextAccount || {});
      setSelectedOrganizationId(nextAccount?.organizationId || "");

      if (isPersonalAccount(nextAccount)) {
        setOrganization(null);
        setOrganizations([]);
        setLocationTypes([]);
        setChangeOrganizationOpen(false);
        setCreateOrganizationOpen(false);
        navigate(paths.workspaceDetails(workspaceId), { replace: true });
        return;
      }

      if (hasAdminAccess((nextAccount?.users || []).find((member) => String(membershipUserId(member)) === String(actorId)))) {
        const [entityPage, typePage] = await Promise.all([
          olreadiaService.organizations.listForUser(actorId, {
            page: 0,
            size: 50,
          }),
          olreadiaService.businessLocationTypes.list({ active: true }),
        ]);

        const nextOrganizations = pageContent(entityPage);
        setOrganizations(nextOrganizations);
        setLocationTypes(pageContent(typePage));
      } else {
        setOrganizations([]);
        setLocationTypes([]);
      }

      if (nextAccount?.organizationId) {
        const entity = await olreadiaService.organizations.get(nextAccount.organizationId);
        setOrganization(entity || {});
      } else {
        setOrganization(null);
      }
    } catch (error) {
      message.error(error?.message || "Failed to load organization details.");
    } finally {
      setLoading(false);
    }
  }, [actorId, navigate, workspaceId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const performLinkOrganization = async (organizationId) => {
    const nextOrganizationId = trimValue(organizationId);
    if (!nextOrganizationId) {
      throw new Error("Select an organization.");
    }

    const updatedAccount = await updateWorkspaceOrganization({
      actorId,
      workspaceId: workspaceId,
      organizationId: nextOrganizationId,
    });
    setAccount(updatedAccount || { ...account, organizationId: nextOrganizationId });
    await refreshWorkspaces();
    await loadData();
    return updatedAccount;
  };

  const linkOrganization = async (organizationId) => {
    try {
      setSavingLink(true);
      await performLinkOrganization(organizationId);
      setChangeOrganizationOpen(false);
      setCreateOrganizationOpen(false);
      message.success("Organization linked.");
    } catch (error) {
      message.error(error?.message || "Failed to link organization.");
    } finally {
      setSavingLink(false);
    }
  };

  const createAndLinkOrganization = async (values) => {
    try {
      setCreating(true);
      const payload = {
        name: trimValue(values.name),
        description: trimValue(values.description),
        ein: trimValue(values.ein),
        businessLocationTypeId: values.businessLocationTypeId,
        status: "DRAFT",
        contactInformation: {
          primaryEmail: trimValue(values.primaryEmail),
          primaryPhone: trimValue(values.primaryPhone),
          websiteUrl: trimValue(values.websiteUrl),
          contactPersonName: trimValue(values.contactPersonName),
        },
      };

      const entity = await olreadiaService.organizations.create(payload, actorId);
      if (!entity?.id) {
        throw new Error("Created organization id is missing.");
      }

      setSavingLink(true);
      await performLinkOrganization(entity.id);
      createForm.resetFields();
      setCreateOrganizationOpen(false);
      message.success("Organization created and linked.");
    } catch (error) {
      message.error(error?.message || "Failed to create organization.");
    } finally {
      setSavingLink(false);
      setCreating(false);
    }
  };

  const linked = Boolean(account?.organizationId);
  const personalWorkspace = isPersonalAccount(account);
  const displayedOrganizationLocation = selectedOrganizationLocation(
    organization,
    account?.organizationLocationId,
  );

  const openCreateOrganization = () => {
    createForm.resetFields();
    setCreateOrganizationOpen(true);
    setOrganizationTab("create");
  };

  const closeCreateOrganization = () => {
    createForm.resetFields();
    setCreateOrganizationOpen(false);
  };

  const openLocationCreate = () => {
    locationForm.resetFields();
    locationForm.setFieldsValue(organizationLocationFormValues(organization || {}));
    setLocationAddressSearch(locationText(organization || {}));
    setSameAsOrganization(true);
    setLocationDrawerOpen(true);
  };

  const closeLocationCreate = () => {
    locationForm.resetFields();
    setLocationAddressSearch("");
    setSameAsOrganization(false);
    setLocationDrawerOpen(false);
  };

  const createLocation = async (values) => {
    if (!account?.organizationId) {
      message.error("Link an organization before adding a location.");
      return;
    }

    try {
      setSavingLocation(true);
      const created = await olreadiaService.organizations.locations.create(
        account.organizationId,
        locationPayloadFromValues(values),
        actorId,
      );
      closeLocationCreate();
      const entity = await olreadiaService.organizations.get(account.organizationId);
      setOrganization(entity || {});
      if (created?.id) {
        await selectOrganizationLocation(created.id);
      }
      message.success("Organization location added.");
    } catch (error) {
      message.error(error?.message || "Failed to add organization location.");
    } finally {
      setSavingLocation(false);
    }
  };

  const selectOrganizationLocation = async (organizationLocationId) => {
    try {
      setSavingLocationSelection(true);
      const updatedAccount = await updateWorkspaceProperties({
        actorId,
        workspaceId,
        updates: [{ name: "organizationLocationId", value: organizationLocationId || null }],
      });
      setAccount(updatedAccount || { ...account, organizationLocationId });
      await refreshWorkspaces();
      message.success("Organization location selected.");
    } catch (error) {
      message.error(error?.message || "Failed to select organization location.");
    } finally {
      setSavingLocationSelection(false);
    }
  };

  const renderCreateOrganizationPrompt = (description) => (
    <Card
      style={{
        borderRadius: 8,
        borderColor: OLIVEEB_COLORS.oliveebBlueSoft,
        background: `linear-gradient(135deg, #ffffff 0%, ${OLIVEEB_COLORS.bgSoft} 100%)`,
      }}
    >
      <Space direction="vertical" size={14} style={{ width: "100%" }}>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={description} />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openCreateOrganization}
          style={{
            borderColor: OLIVEEB_COLORS.oliveebGreen,
            background: OLIVEEB_COLORS.oliveebGreen,
            fontWeight: 700,
          }}
        >
          Create Organization
        </Button>
      </Space>
    </Card>
  );

  const renderCreateOrganizationForm = () => (
    <Card
      size="small"
      style={{
        borderRadius: 8,
        borderColor: OLIVEEB_COLORS.oliveebBlueSoft,
        background: `linear-gradient(135deg, #ffffff 0%, ${OLIVEEB_COLORS.bgSoft} 58%, ${OLIVEEB_COLORS.oliveebBlueSoft} 100%)`,
        boxShadow: "0 14px 34px rgba(0, 64, 56, 0.10)",
      }}
    >
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        <div>
          <Text strong style={{ color: OLIVEEB_COLORS.oliveebDarkBlue }}>
            New Organization
          </Text>
          <Text style={{ display: "block", color: OLIVEEB_COLORS.textMuted, fontSize: 12 }}>
            Create a lightweight organization profile and link it to this workspace.
          </Text>
        </div>
        <Form
          form={createForm}
          layout="vertical"
          requiredMark={false}
          onFinish={createAndLinkOrganization}
        >
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label="Organization name"
                rules={[{ required: true, message: "Enter organization name." }]}
              >
                <Input placeholder="Organization name" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="businessLocationTypeId"
                label="Location type"
                rules={[{ required: true, message: "Select an organization location type." }]}
              >
                <Select
                  showSearch
                  placeholder="Select type"
                  loading={loading}
                  optionFilterProp="label"
                  options={locationTypeOptions}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="primaryEmail" label="Primary email">
                <Input placeholder="organization@example.com" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="primaryPhone" label="Primary phone">
                <Input placeholder="+1..." />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="ein" label="EIN">
                <Input placeholder="Optional EIN" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="websiteUrl" label="Website">
                <Input placeholder="https://..." />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="contactPersonName" label="Contact person">
                <Input placeholder="Contact name" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="description" label="Description">
                <Input.TextArea rows={1} placeholder="Optional description" />
              </Form.Item>
            </Col>
          </Row>
          <Space wrap>
            <Button
              type="primary"
              htmlType="submit"
              icon={<PlusOutlined />}
              loading={creating || savingLink}
              style={{
                borderColor: OLIVEEB_COLORS.oliveebGreen,
                background: OLIVEEB_COLORS.oliveebGreen,
                fontWeight: 700,
              }}
            >
              Create and Link
            </Button>
            <Button onClick={closeCreateOrganization}>Cancel</Button>
          </Space>
        </Form>
      </Space>
    </Card>
  );

  return (
    <div className="project-page">
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3 flex-wrap">
        <WidgetSectionTitle
          icon={<ShopOutlined />}
          title="Organization"
          subtitle="Workspace organization identity, payment owner, and organization link"
          className="flex-1"
          titleStyle={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}
          subtitleStyle={{ color: OLIVEEB_COLORS.oliveebGreen }}
        />
        <Space wrap>
          <Button
            icon={<ArrowLeftOutlined />}
            style={workspaceButtonStyles.back}
            onClick={() => navigate(paths.workspaceDetails(workspaceId))}
          >
            Back
          </Button>
          <Button
            icon={<ReloadOutlined />}
            style={workspaceButtonStyles.refresh}
            onClick={loadData}
          >
            Refresh
          </Button>
        </Space>
      </div>

      {loading && !account ? (
        <Card>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      ) : !account ? (
        <Card>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Workspace not found" />
        </Card>
      ) : (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Card style={workspaceStyles.softCard}>
            <Space size={12} align="start">
              <span
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 8,
                  display: "grid",
                  placeItems: "center",
                  background: workspaceStyles.iconBubble.background,
                  color: workspaceStyles.iconBubble.color,
                  flex: "0 0 auto",
                }}
              >
                <AppstoreOutlined />
              </span>
              <div>
                <Text style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Workspace</Text>
                <Title level={4} style={{ margin: 0, color: OLIVEEB_COLORS.oliveebDarkBlue }}>
                  {account.name || "Workspace"}
                </Title>
                <Text type="secondary">
                  {readable(account.product, "Product")} • {readable(account.accountType, "Workspace type")}
                </Text>
              </div>
            </Space>
          </Card>

          {personalWorkspace ? (
            <Card style={workspaceStyles.softCard}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Personal workspaces do not use organizations"
              >
                <Button
                  icon={<ArrowLeftOutlined />}
                  style={workspaceButtonStyles.back}
                  onClick={() => navigate(paths.workspaceDetails(workspaceId))}
                >
                  Back to Workspace
                </Button>
              </Empty>
            </Card>
          ) : linked ? (
            <>
              <Card
                title={<Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Linked Organization</Text>}
                extra={canManageOrganization ? (
                  <Button
                    icon={<EditOutlined />}
                    style={{
                      borderColor: OLIVEEB_COLORS.oliveebBlue,
                      color: OLIVEEB_COLORS.oliveebBlue,
                      fontWeight: 700,
                    }}
                    onClick={() => {
                      setSelectedOrganizationId(account.organizationId || "");
                      setChangeOrganizationOpen(true);
                    }}
                  >
                    Change Organization
                  </Button>
                ) : null}
                style={{ borderRadius: 8 }}
              >
                {loading && !organization ? (
                  <Skeleton active paragraph={{ rows: 6 }} />
                ) : organization ? (
                  <Space direction="vertical" size={16} style={{ width: "100%" }}>
                    <div className="d-flex align-items-start gap-3 flex-wrap">
                      <span
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: 8,
                          display: "grid",
                          placeItems: "center",
                          background: OLIVEEB_COLORS.greenSoft2,
                          color: OLIVEEB_COLORS.oliveebDarkGreen,
                          fontSize: 20,
                        }}
                      >
                        <BankOutlined />
                      </span>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <Title level={4} style={{ margin: 0 }}>
                          {organizationName(organization, account.organizationId)}
                        </Title>
                        {entityContact(organization) ? (
                          <Text type="secondary" style={{ wordBreak: "break-word" }}>
                            {entityContact(organization)}
                          </Text>
                        ) : null}
                        <Space wrap size={[4, 4]} style={{ marginTop: 8 }}>
                          {organization.status ? (
                            <Tag color="green">{readable(organization.status)}</Tag>
                          ) : null}
                          {organization.businessLocationType ? (
                            <Tag color="blue">
                              {organization.businessLocationType.label ||
                                organization.businessLocationType.code}
                            </Tag>
                          ) : null}
                        </Space>
                      </div>
                    </div>

                    <Descriptions
                      bordered
                      size="small"
                      column={{ xs: 1, md: 2 }}
                      items={[
                        {
                          key: "id",
                          label: "Organization ID",
                          children: organization.id ? (
                            <CopyableText
                              value={organization.id}
                            />
                          ) : (
                            account.organizationId
                          ),
                        },
                        {
                          key: "ein",
                          label: "EIN",
                          children: organization.ein || "-",
                        },
                        {
                          key: "location",
                          label: "Location",
                          children: organizationLocationDisplay(displayedOrganizationLocation) || "-",
                        },
                        {
                          key: "created",
                          label: "Created",
                          children: formatDateTime(organization.createdAt) || "-",
                        },
                        {
                          key: "updated",
                          label: "Updated",
                          children: formatDateTime(organization.updatedAt) || "-",
                        },
                      ]}
                    />
                  </Space>
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Organization details are not available" />
                )}
              </Card>

              <Card
                title={<Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Organization Locations</Text>}
                extra={canManageOrganization ? (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={openLocationCreate}
                    style={{
                      borderColor: OLIVEEB_COLORS.oliveebGreen,
                      background: OLIVEEB_COLORS.oliveebGreen,
                      fontWeight: 700,
                    }}
                  >
                    Add Location
                  </Button>
                ) : null}
                style={{ borderRadius: 8 }}
              >
                {organizationLocations(organization).length ? (
                  <Space direction="vertical" size={10} style={{ width: "100%" }}>
                    {canManageOrganization ? (
                      <Select
                        showSearch
                        allowClear
                        placeholder="Select organization location"
                        value={displayedOrganizationLocation?.id || undefined}
                        loading={savingLocationSelection}
                        onChange={selectOrganizationLocation}
                        optionFilterProp="label"
                        style={{ width: "100%" }}
                        options={organizationLocations(organization).map((location) => ({
                          value: location.id,
                          label: location.name || locationText(location) || location.id,
                        }))}
                      />
                    ) : null}
                    {organizationLocations(organization).map((location) => (
                      <Card key={location.id || location.name} size="small" style={{ borderRadius: 8 }}>
                        <Space direction="vertical" size={4} style={{ width: "100%" }}>
                          <Space wrap size={[6, 6]}>
                            <Text strong>{location.name || "Organization location"}</Text>
                            <Tag color="blue">
                              {locationTypeLabel(location.businessLocationType || location.organizationLocationType || location.type)}
                            </Tag>
                            {location.status ? <Tag color="green">{readable(location.status)}</Tag> : null}
                            {String(displayedOrganizationLocation?.id || "") === String(location.id || "") ? (
                              <Tag color="gold">Selected</Tag>
                            ) : null}
                          </Space>
                          <Text type="secondary">{entityContact(location) || "No contact details"}</Text>
                          <Text type="secondary">{locationText(location) || "No address details"}</Text>
                          {locationCoordinateText(location) ? (
                            <Text type="secondary">Coordinates: {locationCoordinateText(location)}</Text>
                          ) : null}
                          {location.locationInformation?.placeId ? (
                            <Text type="secondary">Place ID: {location.locationInformation.placeId}</Text>
                          ) : null}
                        </Space>
                      </Card>
                    ))}
                  </Space>
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No organization locations added yet" />
                )}
              </Card>

              {canManageOrganization && changeOrganizationOpen ? (
                <Card
                  title={<Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Change Organization</Text>}
                  style={{ borderRadius: 8, ...workspaceStyles.highlightedCard }}
                >
                  <Space direction="vertical" size={16} style={{ width: "100%" }}>
                    <Alert
                      type="info"
                      showIcon
                      message="Select another organization linked to your user."
                    />
                    {organizations.length ? (
                      <>
                        <Select
                          showSearch
                          placeholder="Select organization"
                          value={selectedOrganizationId || undefined}
                          onChange={setSelectedOrganizationId}
                          optionFilterProp="label"
                          style={{ width: "100%" }}
                          options={organizations.map((entity) => ({
                            value: entity.id,
                            label: organizationName(entity, entity.id),
                          }))}
                        />
                        <Space wrap>
                          <Button
                            type="primary"
                            icon={<CheckCircleOutlined />}
                            loading={savingLink}
                            onClick={() => linkOrganization(selectedOrganizationId)}
                            style={{
                              borderColor: OLIVEEB_COLORS.oliveebGreen,
                              background: OLIVEEB_COLORS.oliveebGreen,
                              fontWeight: 700,
                            }}
                          >
                            Update Organization
                          </Button>
                          <Button
                            onClick={() => {
                              setSelectedOrganizationId(account.organizationId || "");
                              setChangeOrganizationOpen(false);
                            }}
                          >
                            Cancel
                          </Button>
                        </Space>
                      </>
                    ) : (
                      createOrganizationOpen
                        ? renderCreateOrganizationForm()
                        : renderCreateOrganizationPrompt(
                            "No organizations linked to your user were found",
                          )
                    )}
                  </Space>
                </Card>
              ) : null}
            </>
          ) : canManageOrganization ? (
            <>
              <Alert
                type="warning"
                showIcon
                message="This workspace is not linked to an organization."
                description="Select one of your existing organizations or create a new one and link it to this workspace."
              />

              <Tabs
                className="pa-top-tabs"
                activeKey={organizationTab}
                onChange={setOrganizationTab}
                items={[
                  {
                    key: "select",
                    label: "Select Existing",
                    children: (
                      <Card style={{ borderRadius: 8 }}>
                        <Space direction="vertical" size={16} style={{ width: "100%" }}>
                          {organizations.length ? (
                            <>
                              <Select
                                showSearch
                                placeholder="Select organization"
                                value={selectedOrganizationId || undefined}
                                onChange={setSelectedOrganizationId}
                                optionFilterProp="label"
                                style={{ width: "100%" }}
                                options={organizations.map((entity) => ({
                                  value: entity.id,
                                  label: organizationName(entity, entity.id),
                                }))}
                              />
                              <Button
                                type="primary"
                                icon={<CheckCircleOutlined />}
                                loading={savingLink}
                                onClick={() => linkOrganization(selectedOrganizationId)}
                                style={{
                                  borderColor: OLIVEEB_COLORS.oliveebGreen,
                                  background: OLIVEEB_COLORS.oliveebGreen,
                                  fontWeight: 700,
                                }}
                              >
                                Link Organization
                              </Button>
                            </>
                          ) : (
                            renderCreateOrganizationPrompt(
                              "No organizations linked to your user were found",
                            )
                          )}
                        </Space>
                      </Card>
                    ),
                  },
                  {
                    key: "create",
                    label: "Add New",
                    children: (
                      createOrganizationOpen
                        ? renderCreateOrganizationForm()
                        : renderCreateOrganizationPrompt(
                            "Create an organization, then link it to this workspace.",
                          )
                    ),
                  },
                ]}
              />
            </>
          ) : (
            <Alert
              type="info"
              showIcon
              message="This workspace is not linked to a dealership."
              description="Workspace owners and admins can link or create a dealership."
            />
          )}
        </Space>
      )}
      <Drawer
        title="Add organization location"
        open={locationDrawerOpen}
        onClose={closeLocationCreate}
        width={520}
        destroyOnClose
      >
        <Form form={locationForm} layout="vertical" requiredMark={false} size="small" onFinish={createLocation}>
          <Form.Item name="latitude" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="longitude" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="placeId" hidden>
            <Input />
          </Form.Item>
          <Space direction="vertical" size={10} style={{ width: "100%" }}>
            <Checkbox
              checked={sameAsOrganization}
              onChange={(event) => {
                setSameAsOrganization(event.target.checked);
                if (event.target.checked) {
                  locationForm.setFieldsValue(organizationAddressFormValues(organization || {}));
                  setLocationAddressSearch(locationText(organization || {}));
                }
              }}
            >
              Same as organization address
            </Checkbox>

            <div>
              <Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Location details</Text>
              <Row gutter={[8, 0]} style={{ marginTop: 8 }}>
                <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label="Location name"
                rules={[{ required: true, message: "Enter location name." }]}
                    style={{ marginBottom: 8 }}
              >
                <Input placeholder="Facility, branch, office, or home" />
              </Form.Item>
                </Col>
                <Col xs={24} md={12}>
              <Form.Item
                name="organizationLocationTypeId"
                label="Location type"
                rules={[{ required: true, message: "Select location type." }]}
                    style={{ marginBottom: 8 }}
              >
                <Select
                  showSearch
                  placeholder="Select type"
                  optionFilterProp="label"
                  options={locationTypeOptions}
                />
              </Form.Item>
                </Col>
                <Col xs={24} md={8}>
              <Form.Item name="status" label="Status">
                <Select
                  options={["ACTIVE", "DRAFT", "INACTIVE"].map((value) => ({
                    value,
                    label: readable(value),
                  }))}
                />
              </Form.Item>
                </Col>
                <Col xs={24} md={16}>
              <Form.Item name="description" label="Description">
                <Input placeholder="Optional location notes" />
              </Form.Item>
                </Col>
              </Row>
            </div>

            <div>
              <Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Contact</Text>
              <Row gutter={[8, 0]} style={{ marginTop: 8 }}>
                <Col xs={24} md={12}>
                  <Form.Item name="primaryEmail" label="Primary email" style={{ marginBottom: 8 }}>
                    <Input placeholder="location@example.com" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="primaryPhone" label="Primary phone" style={{ marginBottom: 8 }}>
                    <Input placeholder="+1..." />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="contactPersonName" label="Contact person" style={{ marginBottom: 8 }}>
                    <Input placeholder="Contact name" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="contactPersonTitle" label="Contact title" style={{ marginBottom: 8 }}>
                    <Input placeholder="Manager, owner, admin" />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item name="websiteUrl" label="Website" style={{ marginBottom: 8 }}>
                    <Input placeholder="https://..." />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            <div>
              <Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Address</Text>
              <div style={{ marginTop: 8, marginBottom: 8 }}>
                {appConfigLoading && !googleMapsApiKey ? (
                  <Input disabled placeholder="Loading map address search..." />
                ) : (
                  <GooglePlacesAutocompleteInput
                    googleMapsApiKey={googleMapsApiKey}
                    placeholder={googleMapsApiKey ? "Search address e.g. 100 Main St, Seattle" : "Enter address"}
                    types={["address"]}
                    value={locationAddressSearch}
                    onChange={(value) => {
                      setLocationAddressSearch(value);
                      setSameAsOrganization(false);
                      if (!googleMapsApiKey) {
                        locationForm.setFieldsValue({ addressLine1: value });
                      }
                    }}
                    onResolvePlace={async ({ details, prediction }) => {
                      const resolvedAddress = details?.formatted_address || prediction?.description || locationAddressSearch || "";
                      setLocationAddressSearch(resolvedAddress);
                      setSameAsOrganization(false);
                      if (details) {
                        const fields = addressFieldsFromPlaceDetails(details);
                        locationForm.setFieldsValue({
                          ...fields,
                          addressLine1: fields.addressLine1 || resolvedAddress,
                          placeId: details.place_id || fields.placeId || null,
                        });
                      } else {
                        locationForm.setFieldsValue({ addressLine1: resolvedAddress });
                      }
                    }}
                  />
                )}
              </div>
              <Row gutter={[8, 0]} style={{ marginTop: 8 }}>
                <Col xs={24}>
                  <Form.Item name="addressLine1" label="Address line 1" style={{ marginBottom: 8 }}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item name="addressLine2" label="Address line 2" style={{ marginBottom: 8 }}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="city" label="City" style={{ marginBottom: 8 }}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="county" label="County" style={{ marginBottom: 8 }}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="state" label="State" style={{ marginBottom: 8 }}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="zipCode" label="Zip code" style={{ marginBottom: 8 }}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item name="country" label="Country" style={{ marginBottom: 8 }}>
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            <Space wrap style={{ justifyContent: "flex-end", width: "100%", paddingTop: 4 }}>
              <Button onClick={closeLocationCreate}>Cancel</Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<PlusOutlined />}
                loading={savingLocation}
                style={{
                  borderColor: OLIVEEB_COLORS.oliveebGreen,
                  background: OLIVEEB_COLORS.oliveebGreen,
                  fontWeight: 700,
                }}
              >
                Save Location
              </Button>
            </Space>
          </Space>
        </Form>
      </Drawer>
    </div>
  );
};

export default WorkspaceOrganizationPage;
